import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import useBilling from "../hooks/useBilling";

function hasFeature(features, key) {
  if (!features) return false;
  const v = features[key];
  if (v == null) return false;
  if (typeof v === "number") return !!v;
  if (typeof v === "string") return !!v;
  return !!v;
}

/**
 * Modern, animated button with subscription/feature gating
 *
 * Props:
 * - required: string[] of feature keys
 * - requireActiveSub: boolean (default true)
 * - to: optional route (renders <Link>)
 * - onClick: optional handler (renders <button>)
 * - icon: optional React component (e.g., Edit3 from lucide-react)
 * - children: button label
 * - variant: "primary" | "secondary" | "ghost" | "danger" | "success"
 * - size: "sm" | "md" | "lg"
 * - className: extra classes
 * - onLockedClick?: optional handler when blocked
 * - showUpgradeBadge: boolean (default true) - show upgrade badge when locked
 * - fullWidth: boolean (default false) - make button full width
 */
export default function FeatureGateButton({
  required = [],
  requireActiveSub = true,
  to,
  onClick,
  icon: Icon,
  children,
  variant = "primary",
  size = "md",
  className = "",
  onLockedClick,
  showUpgradeBadge = true,
  fullWidth = false,
  ...rest
}) {
  const navigate = useNavigate();
  const { me, loading } = useBilling();

  // Loading skeleton with shimmer effect
  if (loading) {
    return (
      <div
        className={[
          "relative overflow-hidden rounded-md bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse",
          size === "sm" ? "h-9 w-28" : size === "lg" ? "h-12 w-40" : "h-10 w-36",
          fullWidth && "w-full",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
      </div>
    );
  }

  const planName = me?.subscription?.plan?.name || "Free";
  const hasActive = !!me?.hasActiveSubscription;
  const missing = required.filter((k) => !hasFeature(me?.features, k));
  const blockedBySub = requireActiveSub && !hasActive;
  const blockedByFeature = missing.length > 0;
  const allowed = !blockedBySub && !blockedByFeature;

  // Base styles
  const base =
    "group relative inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed overflow-hidden";

  // Size variants
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-5 py-3 text-base",
  };

  // Color variants
  const variants = {
    primary: allowed
      ? "bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800 focus:ring-indigo-500 shadow-md hover:shadow-lg active:scale-[0.98]"
      : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 border border-gray-300 cursor-pointer hover:from-gray-200 hover:to-gray-300 hover:shadow-md",
    secondary: allowed
      ? "bg-white text-slate-800 border-2 border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-slate-400 shadow-sm hover:shadow-md active:scale-[0.98]"
      : "bg-white text-gray-400 border-2 border-gray-200 cursor-pointer hover:bg-gray-50 hover:shadow-sm",
    ghost: allowed
      ? "bg-transparent text-slate-800 hover:bg-slate-100 focus:ring-slate-400 active:scale-[0.98]"
      : "bg-transparent text-gray-400 cursor-pointer hover:bg-gray-50",
    danger: allowed
      ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 focus:ring-red-500 shadow-md hover:shadow-lg active:scale-[0.98]"
      : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 border border-gray-300 cursor-pointer hover:shadow-md",
    success: allowed
      ? "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-500 shadow-md hover:shadow-lg active:scale-[0.98]"
      : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 border border-gray-300 cursor-pointer hover:shadow-md",
    black: allowed
      ? "bg-black text-white hover:bg-neutral-800 focus:ring-black shadow-sm hover:shadow-md active:scale-[0.98]"
      : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 border border-gray-300 cursor-pointer hover:from-gray-200 hover:to-gray-300 hover:shadow-md",
  };

  const cls = [
    base,
    sizes[size],
    variants[variant],
    fullWidth && "w-full",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Handle locked state
  const handleLocked = (e) => {
    e.preventDefault();
    if (onLockedClick) return onLockedClick();
    navigate("/dashboard/subscription");
  };

  // Button content
  const Content = (
    <>
      {/* Shimmer effect for allowed state */}
      {allowed && (
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      )}

      {/* Icon */}
      <span className="relative z-10 flex items-center gap-2">
        {allowed && Icon && (
          <Icon className={`${size === "sm" ? "w-4 h-4" : size === "lg" ? "w-5 h-5" : "w-4 h-4"} transition-transform group-hover:scale-110`} />
        )}
        {!allowed && (
          <div className="relative">
            <Lock className={`${size === "sm" ? "w-4 h-4" : size === "lg" ? "w-5 h-5" : "w-4 h-4"} transition-transform group-hover:scale-110 group-hover:rotate-12`} />
            {blockedBySub && (
              <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-500 animate-pulse" />
            )}
          </div>
        )}

        {/* Label */}
        <span className="relative">{children}</span>

        {/* Upgrade badge */}
        {!allowed && showUpgradeBadge && (
          <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm group-hover:shadow-md transition-shadow">
            {blockedBySub ? "Upgrade" : "Locked"}
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        )}
      </span>
    </>
  );

  // Render allowed state
  if (allowed) {
    if (to) {
      return (
        <Link to={to} className={cls} {...rest}>
          {Content}
        </Link>
      );
    }
    return (
      <button type="button" onClick={onClick} className={cls} {...rest}>
        {Content}
      </button>
    );
  }

  // Render blocked state with tooltip
  const tooltipText = [
    requireActiveSub && "Requires an active subscription",
    missing.length > 0 && "Requires feature access",
    `Current plan: ${planName}`,
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <button
      type="button"
      aria-disabled="true"
      onClick={handleLocked}
      className={cls}
      title={tooltipText}
      {...rest}
    >
      {Content}
      
      {/* Pulse ring effect on hover when locked */}
      <span className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="absolute inset-0 rounded-md border-2 border-amber-500 animate-ping" />
      </span>
    </button>
  );
}

// Add custom animation keyframes to your global CSS or Tailwind config:
/*
@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
*/